import { useCallback } from 'react';
import { useToast } from '@frameio/vapor';
import type { WorkspaceProjectSelectCallbackOptions } from '@frameio/workspaces/shared/components/WorkspaceProjectList/types';
import { useGetFolderAssetForExportLocationLazyQuery } from '../../../../gql/queries/generated/GetFolderAssetForExportLocation';
import TEXT from './generated/TEXT/useOnResourceSelect';

const TOAST_DURATION = 5000;

TEXT.DEFINE({
  /** Error message displayed when the upload location is not found */
  title: 'Upload location not found.',
  /** Error message details displayed when the upload location is not found */
  details: 'Please use a different upload location.',
});

/**
 * Hook that provides a function to display an error toast when a resource
 * (project or folder) is not found.
 *
 * @returns A callback function that displays an error toast with appropriate
 * messaging.
 */
export const useResourceNotFoundToast = () => {
  const { addToast } = useToast();

  return useCallback(
    ({
      title = TEXT.title,
      details = TEXT.details,
    }: {
      title?: string;
      details?: string;
    }) => {
      addToast(
        {
          title,
          details,
          variant: 'error',
        },
        { duration: TOAST_DURATION }
      );
    },
    [addToast]
  );
};

type ResourceData =
  | WorkspaceProjectSelectCallbackOptions
  | { folderId: string };

/**
 * Hook that provides a callback for handling folder and workspace project
 * selection. Validates that the selected resource exists by making a network
 * request to fetch folder stats, and displays an error toast if the resource is
 * not found.
 *
 * @returns An object containing the onResourceSelect callback function
 * @returns onResourceSelect - Async function that validates and processes
 * resource selection
 * @returns onResourceSelect.data - The resource data (workspace/project or
 * folder ID) to validate
 * @returns onResourceSelect.onSuccess - Callback invoked when the resource is
 * successfully validated
 */
export const useOnResourceSelect = () => {
  const showResourceNotFoundToast = useResourceNotFoundToast();
  const [getFolderAssetForExportLocation] =
    useGetFolderAssetForExportLocationLazyQuery();

  const onResourceSelect = useCallback(
    async <T extends ResourceData>({
      data,
      onSuccess,
    }: {
      data: T;
      onSuccess: (data: T) => void;
    }) => {
      const folderId =
        'folderId' in data ? data?.folderId : data?.project?.rootAssetId;

      const { error } = await getFolderAssetForExportLocation({
        variables: { folderId: folderId ?? '' },
        fetchPolicy: 'network-only',
      });

      if (error || !folderId) {
        showResourceNotFoundToast({});
        return;
      }

      onSuccess?.(data);
    },
    [getFolderAssetForExportLocation, showResourceNotFoundToast]
  );

  return { onResourceSelect };
};
